// Helpers for the values IndexedDB refuses to store.
//
// IndexedDB writes go through the structured clone algorithm, which rejects anything that is not
// plain data. Our cached objects - plans above all - are plain data by design, but one non-plain
// value left on them by another code path (a fetch Response parked on a route after a failed
// request is enough) fails the whole write with a DataCloneError, and the object silently stops
// being cached. That only shows up much later, as stale data offline.

// Dexie wraps the DOMException, keeping the name on the wrapper and the original in `inner`.
export function isDataCloneError(error: any): boolean {
    return error?.name === 'DataCloneError' || error?.inner?.name === 'DataCloneError';
}

/**
 * Path of the first value structured clone chokes on, e.g.
 * "plan.routes.0.weatherRoutingInfo.response (Response)", or "" when the value can be stored.
 *
 * Walks only the branch that fails and stops at the first offender: this is a debugging aid on an
 * error path, meant to name the property to fix, not to audit the whole object.
 */
export function firstUncloneablePath(value: any, path: string): string {
    // Read off globalThis: the DOM typings of the TypeScript version in use here do not declare it.
    const structuredCloneFn: ((value: any) => any) | undefined = (<any>globalThis).structuredClone;
    if (typeof structuredCloneFn !== 'function') {
        return 'unknown (structuredClone unavailable)';
    }

    try {
        structuredCloneFn(value);
        return '';
    } catch {
        if (value === null || typeof value !== 'object') {
            return `${path} (${typeof value})`;
        }

        for (const [key, child] of Object.entries(value)) {
            const offender = firstUncloneablePath(child, `${path}.${key}`);
            if (offender) {
                return offender;
            }
        }

        // Nothing below is to blame, so it is this value itself (a Response, a Blob, a class
        // instance, a function...).
        return `${path} (${value?.constructor?.name ?? 'object'})`;
    }
}

// A copy of the value with everything IndexedDB cannot store dropped. JSON is a faithful copy for
// the data we cache: it arrives from the APIs as JSON and is sent back as JSON, so nothing that
// matters is lost.
export function toStorableCopy<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}
